import { Injectable, inject, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

/**
 * Niveaux de log supportés.
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Entrée de log structurée envoyée au backend.
 */
interface LogEntry {
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
  route?: string;
  timestamp: string;
}

/**
 * Service de logging centralisé.
 *
 * - Dev : écrit dans la console (console.debug/info/warn/error)
 * - Prod : supprime debug, batche warn+error → POST /api/client-logs
 *
 * Usage dans un composant ou service :
 *   private log = inject(LogService);
 *   this.log.info('Event created', { eventId });
 *   this.log.error('Failed to load', { error: err.message });
 */
@Injectable({ providedIn: 'root' })
export class LogService {
  private http = inject(HttpClient);

  /** Buffer pour le batch shipping en production */
  private buffer: LogEntry[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  /** Intervalle de flush en ms (5 secondes) */
  private readonly FLUSH_INTERVAL = 5_000;
  /** Taille max du buffer avant flush immédiat */
  private readonly BUFFER_MAX = 20;

  private readonly isProd = environment.production;

  // ── Public API ─────────────────────────────────────────────

  debug(message: string, ...args: unknown[]): void {
    if (this.isProd) return; // supprimé en prod
    console.debug(`[DEBUG] ${message}`, ...args);
  }

  info(message: string, ...args: unknown[]): void {
    if (!this.isProd) {
      console.info(`[INFO] ${message}`, ...args);
    }
    // Info n'est pas envoyé au backend (trop verbeux)
  }

  warn(message: string, ...args: unknown[]): void {
    if (!this.isProd) {
      console.warn(`[WARN] ${message}`, ...args);
    }
    this.enqueue('warn', message, this.toContext(args));
  }

  error(message: string, ...args: unknown[]): void {
    if (!this.isProd) {
      console.error(`[ERROR] ${message}`, ...args);
    }
    this.enqueue('error', message, this.toContext(args));
  }

  // ── Helpers ────────────────────────────────────────────────

  /** Normalise les args en un Record<string, unknown> pour le shipping. */
  private toContext(args: unknown[]): Record<string, unknown> | undefined {
    if (args.length === 0) return undefined;
    if (
      args.length === 1 &&
      typeof args[0] === 'object' &&
      args[0] !== null &&
      !Array.isArray(args[0])
    ) {
      return args[0] as Record<string, unknown>;
    }
    return { data: args.length === 1 ? args[0] : args };
  }

  // ── Batch shipping ─────────────────────────────────────────

  private enqueue(
    level: LogLevel,
    message: string,
    context?: Record<string, unknown>,
  ): void {
    if (!this.isProd) return; // en dev, console suffit

    this.buffer.push({
      level,
      message,
      context,
      route: globalThis.location?.pathname,
      timestamp: new Date().toISOString(),
    });

    if (this.buffer.length >= this.BUFFER_MAX) {
      this.flush();
    } else if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => this.flush(), this.FLUSH_INTERVAL);
    }
  }

  private flush(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    if (this.buffer.length === 0) return;

    const entries = [...this.buffer];
    this.buffer = [];

    // Fire-and-forget — ne pas bloquer l'UI
    this.http.post(`${environment.apiUrl}/client-logs`, { entries }).subscribe({
      error: () => {
        // Silently ignore — on ne peut pas logger une erreur de logging
      },
    });
  }
}
